/**
 * Doorbell screen — receive visitor rings + respond.
 * Game style: a visitor is a "pokemon-style encounter" with options:
 * A = open the door · B = reject · START (or chat button) = open chat.
 *
 * The screen also exposes a "history" sub-screen via SELECT button.
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import { useApp } from '@/state/store';
import { visitsApi, type RingEvent, ApiError } from '@/lib/api';
import { acceptRing } from '@/lib/ringChat';
import { sfx } from '@/lib/audio';
import { BellSprite, CheckSprite, XSprite, LoadingSprite } from '@/components/Sprites';
import { formatRelative } from '@/lib/format';

export function DoorbellScreen() {
  const { goto, showToast, openChat } = useApp();
  const [rings, setRings] = useState<RingEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<'active' | 'history'>('active');
  const [history, setHistory] = useState<RingEvent[]>([]);
  const [responding, setResponding] = useState<string | null>(null);
  const [openingChat, setOpeningChat] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await visitsApi.active();
      setRings(res.rings || []);
    } catch { /* */ }
    finally { setLoading(false); }
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      const res = await visitsApi.history();
      setHistory(res.rings || []);
    } catch { /* */ }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (tab === 'history') loadHistory();
  }, [tab, loadHistory]);

  // Poll for new active rings every 5s when on active tab
  useEffect(() => {
    if (tab !== 'active') return;
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [tab, load]);

  const respond = async (ring: RingEvent, action: 'open_door' | 'reject') => {
    if (responding) return;
    sfx.tick();
    setResponding(ring.id);
    try {
      const res = await visitsApi.respond(ring.id, action);
      if (res.success) {
        if (action === 'open_door') {
          sfx.doorOpen();
          showToast('Puerta abierta al visitante', 'success');
        } else {
          sfx.cancel();
          showToast('Llamada rechazada', 'info');
        }
      } else {
        sfx.error();
        showToast(mapRingError(res.error || 'Error al responder'), 'error');
      }
      // Always refresh — backend may have moved the ring out of 'ringing' status
      // regardless of whether the local respond call succeeded
      await load();
    } catch (err) {
      sfx.error();
      const msg = err instanceof ApiError ? mapRingError(err.message) : 'Error de red';
      showToast(msg, 'error');
      // Refresh even on network error so a phantom ring is removed
      await load();
    } finally {
      setResponding(null);
    }
  };

  // A = open / first ring, B = reject / back, SELECT = toggle tab, START = chat
  const openChatForRing = useCallback(async (ring: RingEvent) => {
    if (openingChat || responding) return;
    sfx.tick();
    setOpeningChat(ring.id);
    try {
      // Mark ring as answered so the visitor side sees we picked up.
      // Only call acceptRing for 'ringing' rings — 'answered'/'opened_door'
      // rings are ongoing chats the user is returning to (press back from
      // ChatScreen and re-opens). Re-calling accept would 409.
      // Ignore failure: the chat screen will still work and the ring will
      // be answered optimistically client-side.
      if (ring.status === 'ringing' || !ring.status) {
        try { await acceptRing(ring.id); } catch { /* best-effort */ }
      }
      openChat(ring.id);
    } finally {
      setOpeningChat(null);
    }
  }, [openingChat, responding, openChat]);

  const handlersRef = useRef({ tab, rings, responding, respond, setTab, goto, openChatForRing });
  handlersRef.current = { tab, rings, responding, respond, setTab, goto, openChatForRing };

  useEffect(() => {
    const onA = async () => {
      const h = handlersRef.current;
      if (h.tab === 'active' && h.rings[0] && !h.responding) {
        await h.respond(h.rings[0], 'open_door');
      }
    };
    const onB = async () => {
      const h = handlersRef.current;
      if (h.tab === 'active' && h.rings[0] && !h.responding) {
        await h.respond(h.rings[0], 'reject');
      } else {
        h.goto('home');
      }
    };
    const onStart = async () => {
      const h = handlersRef.current;
      if (h.tab === 'active' && h.rings[0] && !h.responding) {
        await h.openChatForRing(h.rings[0]);
      }
    };
    const onSelect = () => handlersRef.current.setTab(handlersRef.current.tab === 'active' ? 'history' : 'active');
    window.addEventListener('vicin-gb:a', onA);
    window.addEventListener('vicin-gb:b', onB);
    window.addEventListener('vicin-gb:start', onStart);
    window.addEventListener('vicin-gb:select', onSelect);
    return () => {
      window.removeEventListener('vicin-gb:a', onA);
      window.removeEventListener('vicin-gb:b', onB);
      window.removeEventListener('vicin-gb:start', onStart);
      window.removeEventListener('vicin-gb:select', onSelect);
    };
  }, []);

  return (
    <div className="gb-screen">
      <div className="gb-status">
        <div className="gb-status-left">
          <BellSprite size={11} />
          <span className="gb-pix gb-pix-xs">TIMBRE</span>
        </div>
      </div>

      <div className="screen-tabs" data-testid="doorbell-tabs">
        <button
          onClick={() => setTab('active')}
          className="screen-tab"
          data-selected={tab === 'active'}
          data-testid="doorbell-tab-active"
        >
          ▶ EN VIVO
        </button>
        <button
          onClick={() => setTab('history')}
          className="screen-tab"
          data-selected={tab === 'history'}
          data-testid="doorbell-tab-history"
        >
          HISTORIAL
        </button>
      </div>

      <div className="gb-body">
        {loading ? (
          <div className="gb-center">
            <LoadingSprite size={32} />
            <div className="gb-pix gb-pix-sm">CARGANDO...</div>
          </div>
        ) : tab === 'active' ? (
          rings.length === 0 ? (
            <div className="gb-center" style={{ gap: 8 }}>
              <BellSprite size={48} />
              <div className="gb-pix gb-pix-sm">TODO TRANQUILO</div>
              <div className="gb-pix gb-pix-xs gb-mid-text" style={{ textAlign: 'center', maxWidth: 200 }}>
                Cuando alguien timbre, va a aparecer acá.
              </div>
            </div>
          ) : (
            <div className="gb-stack" style={{ gap: 6 }}>
              {rings.map((r) => {
                // Ongoing chats (answered/opened_door) are rings the user
                // previously engaged with — show a single "rejoin" action
                // so they can return to the chat instead of staring at
                // "TODO TRANQUILO" while the visitor sends new messages.
                const isOngoing = r.status === 'answered' || r.status === 'opened_door';
                return (
                <div key={r.id} className="gb-dialog" data-testid={`ring-${r.id}`}>
                  <div className="gb-dialog-title">
                    <span style={{ color: isOngoing ? 'var(--gb-mid)' : 'var(--gb-dark)' }}>
                      {isOngoing ? '●' : <span className="gb-blink">▶</span>}
                    </span>
                    <span>{r.visitor_name || 'VISITANTE'}</span>
                    {isOngoing && (
                      <span style={{ marginLeft: 4, fontSize: 5, color: 'var(--gb-mid)' }}>
                        CHAT
                      </span>
                    )}
                    <span style={{ marginLeft: 'auto', fontSize: 5, color: 'var(--gb-mid)' }}>
                      {formatRelative(r.created_at)}
                    </span>
                  </div>
                  {r.visitor_note && (
                    <div className="gb-pix gb-pix-xs gb-mid-text" style={{ fontSize: 6, marginBottom: 4 }}>
                      "{r.visitor_note}"
                    </div>
                  )}
                  {/* Action bar — stacked vertically so each button gets full
                      width and a tall enough tap target (≥18px in LCD pixels,
                      ~36px on screen at 2x). The 160px-wide LCD can't fit
                      3 horizontal buttons with [A]/[S]/[B] labels without
                      truncating or cramping them. Stacking is the GB-true
                      way to do it (single-column "menu" pattern). */}
                  <div className="ring-actions" style={{ marginTop: 6 }}>
                    {isOngoing ? (
                      <button
                        onClick={() => openChatForRing(r)}
                        disabled={openingChat === r.id}
                        className="ring-action ring-action--primary"
                        data-testid="ring-rejoin"
                      >
                        [S] CONTINUAR CHAT
                      </button>
                    ) : (
                      <>
                        <button
                          onClick={() => openChatForRing(r)}
                          disabled={openingChat === r.id}
                          className="ring-action ring-action--primary"
                          data-testid="ring-chat"
                        >
                          [S] CHAT — HABLAR CON VISITANTE
                        </button>
                        <button
                          onClick={() => respond(r, 'open_door')}
                          disabled={responding === r.id}
                          className="ring-action ring-action--confirm"
                          data-testid="ring-open"
                        >
                          [A] ABRIR PUERTA
                        </button>
                        <button
                          onClick={() => respond(r, 'reject')}
                          disabled={responding === r.id}
                          className="ring-action ring-action--danger"
                          data-testid="ring-reject"
                        >
                          [B] RECHAZAR LLAMADA
                        </button>
                      </>
                    )}
                  </div>
                </div>
                );
              })}
            </div>
          )
        ) : (
          history.length === 0 ? (
            <div className="gb-center">
              <div className="gb-pix gb-pix-sm gb-mid-text">SIN HISTORIAL</div>
            </div>
          ) : (
            <div className="gb-stack" style={{ gap: 3, overflowY: 'auto' }}>
              {history.map((r) => (
                <div key={r.id} className="gb-row" style={{
                  padding: '4px 6px',
                  border: '1px solid var(--gb-mid)',
                  fontSize: 6,
                  gap: 6,
                  background: 'var(--gb-bg)',
                }}>
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {r.visitor_name || 'Anónimo'}
                  </span>
                  <span style={{ color: statusColor(r.status) }}>{statusLabel(r.status)}</span>
                  <span style={{ color: 'var(--gb-mid)' }}>{formatRelative(r.created_at)}</span>
                </div>
              ))}
            </div>
          )
        )}

        <div className="gb-pix gb-pix-xs gb-mid-text" style={{ fontSize: 5, textAlign: 'center', marginTop: 'auto', paddingTop: 4, display: 'none' }}>
          [SEL] {tab === 'active' ? 'HISTORIAL' : 'EN VIVO'} · [B] VOLVER
        </div>
      </div>
    </div>
  );
}

function statusColor(s: RingEvent['status']): string {
  switch (s) {
    case 'opened_door': return 'var(--gb-dark)';
    case 'answered': return 'var(--gb-mid)';
    case 'rejected': return 'var(--gb-dark)';
    case 'ringing': return 'var(--gb-dark)';
    default: return 'var(--gb-mid)';
  }
}

function mapRingError(raw: string): string {
  const m = raw.toLowerCase();
  if (m.includes('no encontrado') || m.includes('not found')) return 'El timbre ya expiró.';
  if (m.includes('ya está en estado') || m.includes('already')) return 'Ya no estás timbrando.';
  if (m.includes('no autorizado') || m.includes('unauthorized')) return 'No podés responder este timbre.';
  if (m.includes('no respondió') || m.includes('hardware')) return 'La puerta no respondió. Probá de nuevo.';
  if (m.includes('no está configurada') || m.includes('not configured')) return 'La puerta no está configurada.';
  if (m.includes('demasiados') || m.includes('rate')) return 'Esperá un momento antes de intentar de nuevo.';
  return raw;
}

function statusLabel(s: RingEvent['status']): string {
  switch (s) {
    case 'opened_door': return '✓ ABIERTA';
    case 'answered': return 'ATENDIDA';
    case 'rejected': return '✗ RECHAZADA';
    case 'ringing': return '⏱ EN CURSO';
    case 'timeout': return '⏰ TIMEOUT';
    case 'cancelled': return 'CANCELADA';
    default: return s.toUpperCase();
  }
}

